import gql from 'graphql-tag';
import { USER_MINI_FRAGMENT } from 'libs/graphql/fragment/userFragment';

export const WORLD_MAP_SET_FRAGMENT = gql`
    fragment FWorldMapSet on WorldMapSet {
        worldMapSetId
        title
        isVisibleOnLoad
        imageUrl
        color
        type
        orderIndex
        markers {
            worldMapMarkerId
        }
    }
`;

export const WORLD_MAP_MARKER_FRAGMENT = gql`
    fragment FWorldMapMarker on WorldMapMarker {
        layerTitle
        worldMapMarkerId
        gptContextId
        worldMapSets {
            worldMapSetId
        }
        story {
            storyId
            header {
                title
                imageUrl
                text
                imageStyleId
            }
        }
        stories {
            storyId
            header {
                title
                imageUrl
                text
                imageStyleId
            }
        }
    }
`;

export const WORLD_MARKER_FRAGMENT = gql`
    fragment FWorldMarker on WorldMapMarker {
        worldMapMarkerId
        lat
        lng
        placeId
        placeText
        layerTitle
        locked
        scale
        isGroupable
        visible
        scalable
        rotate
        roomName
        isExternalPoint
        isRoomMarker
        gptContextId
        order
        type
        story {
            storyId
        }
        stories {
            storyId
        }
        hasBookingConfiguration
        isBookingAvailable
        data {
            ... on WorldMapMarkerDataPin {
                label
                img
                emoji
                dotColor
                icon {
                    backgroundColor
                    iconColor
                    iconText
                }
            }
            ... on WorldMapMarkerDataYoutube {
                video
                borderRadiusPx
                borderSize
                borderColor
                dropShadowCombined
            }
            ... on WorldMapMarkerDataText {
                text
                width
                isFilled: fill
                color
                textColor
                textShadow
                borderRadiusPx
                borderSize
                borderColor
                dropShadowCombined
            }
            ... on WorldMapMarkerDataImage {
                src
                lat
                lng
                srcWidth
                borderRadiusPx
                borderSize
                borderColor
                dropShadowCombined
                renderAsLayer
                layerData {
                    topLeft {
                        lat
                        lng
                    }
                    topRight {
                        lat
                        lng
                    }
                    bottomLeft {
                        lat
                        lng
                    }
                    bottomRight {
                        lat
                        lng
                    }
                }
            }
            ... on WorldMapMarkerDataRoute {
                coordinates {
                    lat
                    lng
                }
                fillColor
                fillColorOpacity
                lineColor
                lineOpacity
                dropShadowColor
            }
            ... on WorldMapMarkerDataDraw {
                type
                stringPath: path
                width
                height
                opacity
                strokeWidth
                color
                isFilled: fill
            }
            ... on WorldMapMarkerDataPolygon {
                stringPath: path
                coordinates {
                    lat
                    lng
                }
                width
                height
                color
                isFilled: fill
                renderAs3d
                data3d {
                    level
                    height
                    baseHeight
                    color
                }
            }
            ... on WorldMapMarkerDataDirection {
                start {
                    lat
                    lng
                }
                end {
                    lat
                    lng
                }
                coordinates {
                    lat
                    lng
                }
                path {
                    lat
                    lng
                }
                transport
                width
                color
                opacity
                dropShadowColor
            }
            ... on WorldMapMarkerDataLink {
                link
                text
                width
                color
                borderRadiusPx
                borderSize
                borderColor
                dropShadowCombined
            }
            ... on WorldMapMarkerDataReact {
                componentTypeId
                componentData {
                    name
                    value
                }
            }
            ... on WorldMapMarkerDataLineV2 {
                coordinates {
                    lat
                    lng
                }
                opacity
                width
                color
                dashed {
                    isDashed
                    gapLength
                    lineLength
                }
                rawPaintAttributes
                rawLayoutAttributes
            }
            ... on WorldMapMarkerDataRouteV2 {
                coordinates {
                    lat
                    lng
                }
                opacity
                width
                color
                dashed {
                    isDashed
                    gapLength
                    lineLength
                }
            }
            ... on WorldMapMarkerDataPolygonV2 {
                coordinates {
                    lat
                    lng
                }
                fill {
                    isFilled
                    color
                    opacity
                    rawPaintAttributes
                    rawLayoutAttributes
                }
                border {
                    hasBorder
                    color
                    width
                    opacity
                    rawPaintAttributes
                    rawLayoutAttributes
                }
            }
            ... on WorldMapMarkerDataIconV2 {
                imageUrl
                text
                imageSize
                textSize
                textColor
                textHaloColor
                textHaloWidth
                generatedBackgroundColor
                generatedImageUrl
                generatedIconUrl
                generatedEmoji
                generatedIconColor
                rawPaintAttributes
                rawLayoutAttributes
            }
            ... on WorldMapMarkerDataVariantIconV2 {
                text
                color
                imageUrl
                circleText
                withStar
                useStoryImage
                eventDate
            }
            ... on WorldMapMarkerDataWallV2 {
                coordinates {
                    lat
                    lng
                }
                nonTransformedCoordinates {
                    lat
                    lng
                }
                rotation
                scale
                label
                wall {
                    color
                    height
                    baseHeight
                    opacity
                }
                line {
                    isLine
                    hasFloor
                    fillColor
                    width
                }
            }
            ... on WorldMapMarkerDataImageV2 {
                imageUrl
                coordinates {
                    lat
                    lng
                }
                nonRotatedCoordinates {
                    lat
                    lng
                }
                rotation
                opacity
                flipHorizontal
                flipVertical
                generatedBackgroundColor
                generatedImageUrl
                generatedIconUrl
                generatedEmoji
                generatedIconColor
            }
            ... on WorldMapMarkerDataTextV2 {
                imageUrl
                coordinates {
                    lat
                    lng
                }
                nonRotatedCoordinates {
                    lat
                    lng
                }
                rotation
                generatedText
                generatedTextColor
                generatedBackgroundColor
                generatedWidth
                lineLength
                rawPaintAttributes
                rawLayoutAttributes
            }
            ... on WorldMapMarkerDataIndoorStand {
                text
                textColor
                textHaloColor
                textHaloWidth
                pinSrc
                imageSrc
                pinGeneratedType
                pinGeneratedImageUrl
                pinGeneratedIconUrl
                pinGeneratedIconColor
                logoGeneratedImageUrl
                logoGeneratedStyleId
            }
        }
    }
`;

export const WORLD_MAP_FRAGMENT = gql`
    fragment FWorldMap on WorldMap {
        worldMapId
        title
        user {
            ...FUserMiniInfo
        }
        markerDetailStyle
        showOnMapZoom
        allowGeolocation
        customMapUrl
        groupMarkerColor
        groupMarkerBackground
        groupMarkerClusterMaxZoom
        groupMarkerClusterRadius
        fillCountryCodes
        fillCountryColor
        fillCountryOpacity
        autoHideIcons
        imageBackgroundUrl
        imageBackgroundCoordinates {
            lat
            lng
        }
        visibility
        worldMapSets {
            worldMapSetId
            isVisibleOnLoad
        }
        imageGallery
        iconGallery
        showHighlightedCategory
        showList
        showSearch
        initial {
            lat
            lng
            zoom
            useBounds
            rotation
            pitch
            minZoom
            maxZoom
            bounds {
                topLeft {
                    lat
                    lng
                }
                bottomRight {
                    lat
                    lng
                }
                padding
            }
            autoBounds {
                topLeft {
                    lat
                    lng
                }
                bottomRight {
                    lat
                    lng
                }
                padding
            }
        }
        initialMobile {
            lat
            lng
            zoom
            useBounds
            rotation
            pitch
            minZoom
            maxZoom
            bounds {
                topLeft {
                    lat
                    lng
                }
                bottomRight {
                    lat
                    lng
                }
                padding
            }
        }
        initialType
        defaultStyles
        currentPeriodViews
        markers {
            ...FWorldMarker
        }
    }
    ${USER_MINI_FRAGMENT}
    ${WORLD_MARKER_FRAGMENT}
`;

export const WORLD_MAP_NO_MARKERS_FRAGMENT = gql`
    fragment FWorldMapNoMarkers on WorldMap {
        worldMapId
        title
        user {
            ...FUserMiniInfo
        }
        markerDetailStyle
        customMapUrl
        groupMarkerColor
        groupMarkerBackground
        groupMarkerClusterMaxZoom
        groupMarkerClusterRadius
        fillCountryCodes
        fillCountryColor
        fillCountryOpacity
        autoHideIcons
        imageBackgroundUrl
        imageBackgroundCoordinates {
            lat
            lng
        }
        showOnMapZoom
        allowGeolocation
        visibility
        worldMapSets {
            worldMapSetId
            isVisibleOnLoad
        }
        imageGallery
        showHighlightedCategory
        showList
        showSearch
        iconGallery
        initial {
            lat
            lng
            zoom
            useBounds
            rotation
            pitch
            minZoom
            maxZoom
            bounds {
                topLeft {
                    lat
                    lng
                }
                bottomRight {
                    lat
                    lng
                }
                padding
            }
            autoBounds {
                topLeft {
                    lat
                    lng
                }
                bottomRight {
                    lat
                    lng
                }
                padding
            }
        }
        initialMobile {
            lat
            lng
            zoom
            useBounds
            rotation
            pitch
            minZoom
            maxZoom
            bounds {
                topLeft {
                    lat
                    lng
                }
                bottomRight {
                    lat
                    lng
                }
                padding
            }
        }
        initialType
        defaultStyles
        currentPeriodViews
    }
    ${USER_MINI_FRAGMENT}
`;

export const WORLD_MAP_VIEW_NO_MARKERS_FRAGMENT = gql`
    fragment FWorldMapViewNoMarkers on WorldMapView {
        worldMapId
        title
        user {
            ...FUserMiniInfo
        }
        markerDetailStyle
        customMapUrl
        groupMarkerColor
        groupMarkerBackground
        groupMarkerClusterMaxZoom
        groupMarkerClusterRadius
        fillCountryCodes
        fillCountryColor
        fillCountryOpacity
        autoHideIcons
        imageBackgroundUrl
        showHighlightedCategory
        showList
        showSearch
        imageBackgroundCoordinates {
            lat
            lng
        }
        showOnMapZoom
        allowGeolocation
        visibility
        worldMapSets {
            worldMapSetId
            isVisibleOnLoad
        }
        initial {
            lat
            lng
            zoom
            useBounds
            rotation
            pitch
            minZoom
            maxZoom
            bounds {
                topLeft {
                    lat
                    lng
                }
                bottomRight {
                    lat
                    lng
                }
                padding
            }
            autoBounds {
                topLeft {
                    lat
                    lng
                }
                bottomRight {
                    lat
                    lng
                }
                padding
            }
        }
        initialMobile {
            lat
            lng
            zoom
            useBounds
            rotation
            pitch
            minZoom
            maxZoom
            bounds {
                topLeft {
                    lat
                    lng
                }
                bottomRight {
                    lat
                    lng
                }
                padding
            }
        }
        initialType
        defaultStyles
    }
    ${USER_MINI_FRAGMENT}
`;
